import { NgClass, UpperCasePipe } from "@angular/common";
import {
  Component,
  computed,
  inject,
  signal,
  ElementRef,
  ViewChild,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterOutlet,
} from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import {
  ToggleComponent,
  ToggleSize,
} from "@claas/claas-ui-components/form/toggle";
import { TranslatePipe } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { ArgumentRatingIcon } from "../../../shared/components/argument-rating-icon/argument-rating-icon";
import { HorizontalScrollHelperComponent } from "../../../shared/components/horizontal-scroll-helper/horizontal-scroll-helper";
import { MainModalDialog } from "../../../shared/components/main-modal-dialog/main-modal-dialog";
import {
  pathRouteDataHelper$,
  pathRouteParametersHelper$,
} from "../../../shared/helpers/path-params.helper";
import { DefaultArgument } from "../../../shared/pia-api/generated";
import { FavoriteArgumentsService } from "../../../shared/services/favorite-arguments.service";
import { ModalCommunicationService } from "../../../shared/services/modal-communication-service";
import { CompetitorCompareSelect } from "../competitor-compare-select/competitor-compare-select";
import { ProductCompareSelect } from "../product-compare-select/product-compare-select";

@Component({
  selector: "claas-products-compare-arguments",
  imports: [
    ToggleComponent,
    TranslatePipe,
    ProductCompareSelect,
    CompetitorCompareSelect,
    ArgumentRatingIcon,
    RouterLink,
    UpperCasePipe,
    IconComponent,
    NgClass,
    RouterOutlet,
    MainModalDialog,
    HorizontalScrollHelperComponent,
  ],
  templateUrl: "./products-compare-arguments.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./products-compare-arguments.scss",
})
export class ProductsCompareArguments implements OnDestroy, OnInit {
  private favoriteArgumentsService = inject(FavoriteArgumentsService);
  private router = inject(Router);
  protected readonly toggleSize = ToggleSize;
  route = inject(ActivatedRoute);
  modalCommunicationService = inject(ModalCommunicationService);

  @ViewChild("scrollContainer", { static: false })
  scrollContainer!: ElementRef<HTMLDivElement>;

  showMainModal = false;

  private subscription?: Subscription;

  data = toSignal(pathRouteDataHelper$());
  params = toSignal(pathRouteParametersHelper$());

  countryID = computed(() => this.params()?.["country"]);
  languageID = computed(() => this.params()?.["language"]);
  year = computed(() => this.params()?.["year"]);
  productID = computed(() => this.params()?.["id"]);
  productDetailID = computed(() => this.params()?.["productId"]);
  competitorID = computed(() => this.params()?.["competitorId"]);

  // Hover states for favorite buttons
  hoveredProductArgument = signal<string | null>(null);

  argumentsComparison = computed(
    (): {
      title: string;
      productArguments: DefaultArgument[];
      competitorArguments: DefaultArgument[];
    }[] => {
      const [productArguments, competitorArguments]: DefaultArgument[][] =
        this.data()?.["argumentsComparison"] ?? [];

      if (
        !productArguments ||
        productArguments.length === 0 ||
        !competitorArguments ||
        competitorArguments.length === 0
      ) {
        return [];
      }

      // Filter and group product arguments based on favorites
      const getGroupedProductMap = (currentArguments: DefaultArgument[]) => {
        return currentArguments
          .filter((currentArgument) =>
            !this.showFavoriteArgumentsStatus()
              ? true
              : currentArgument?.ID &&
                this.favoriteProductArguments()?.includes(currentArgument.ID),
          )
          .reduce(
            (acc, item) => {
              const category = item.Category as string;
              if (!acc[category]) {
                acc[category] = [];
              }
              item.Rating = item.Rating ? Math.round(item.Rating) : 0;
              acc[category].push(item);
              return acc;
            },
            {} as Record<string, DefaultArgument[]>,
          );
      };

      const groupedProductMap = getGroupedProductMap(productArguments);
      const groupedProductMapByArgumentCategory = Object.entries(
        groupedProductMap,
      )
        .map(([category, items]) => {
          const productArgumentsSorted = items.sort(
            (a, b) => Number(b.Sort) - Number(a.Sort),
          );

          if (!productArgumentsSorted?.length) {
            return null;
          }

          const competitorArgumentsSelected = competitorArguments.reduce(
            (previousCompetitorArguments, competitorArgument) => {
              if (competitorArgument.Competitor) {
                const index = productArgumentsSorted.findIndex(
                  (productArgumentSorted) =>
                    productArgumentSorted.ID === competitorArgument.Competitor,
                );
                previousCompetitorArguments[index] = competitorArgument;
              }

              return previousCompetitorArguments;
            },
            new Array(items.length).fill(null) as (DefaultArgument | null)[],
          );

          return {
            title: category,
            productArguments: productArgumentsSorted,
            competitorArguments: competitorArgumentsSelected,
          };
        })
        .filter(Boolean) as {
        title: string;
        productArguments: DefaultArgument[];
        competitorArguments: DefaultArgument[];
      }[];

      return groupedProductMapByArgumentCategory;
    },
  );

  showFavoriteArgumentsStatus = toSignal<boolean>(
    this.favoriteArgumentsService.productArgumentsFavoriteStatus$,
  );
  favoriteProductArguments = toSignal(
    this.favoriteArgumentsService.includedDocumentIds$,
  );

  isProductArgumentFavorite = computed(
    () => (id?: string) => id && this.favoriteProductArguments()?.includes(id),
  );

  handleToggleFavouriteArgumentsChange(checked: boolean) {
    this.favoriteArgumentsService.setFavoritesArgumentsStatus(checked);
  }

  addArgumentToFavorites(id?: string): void {
    if (!id) return;
    if (!this.favoriteProductArguments()?.includes(id)) {
      this.favoriteArgumentsService.add(id);
    } else {
      this.favoriteArgumentsService.remove(id);
    }
  }

  setProductArgumentHover(id: string | null): void {
    this.hoveredProductArgument.set(id);
  }

  /**
   * delegate row click to router target within the found a tag
   * checks if the favorite button was the clicked element to prevent navigation when
   * favorite button was clicked
   * also checks if there's a valid title before allowing navigation
   * @param evt
   */
  async onProductArgumentRowClick(
    evt: MouseEvent,
    argument: DefaultArgument | undefined,
  ) {
    // Don't navigate if there's no title
    if (!argument?.Title) {
      return;
    }
    const { currentTarget, target } = evt;
    const anchorElement = (currentTarget as HTMLElement)?.querySelector(
      "a",
    ) as HTMLAnchorElement;
    const anchorDelegateTarget = anchorElement?.href;
    const favoriteButtonElement = (currentTarget as HTMLElement)?.querySelector(
      "button",
    ) as HTMLButtonElement;

    if (
      anchorDelegateTarget &&
      target &&
      !(
        target === favoriteButtonElement ||
        favoriteButtonElement?.contains(target as HTMLElement)
      )
    ) {
      const url = new URL(anchorDelegateTarget);
      // Extract the path after the base URL
      const pathname = url.pathname;
      const searchParams = Object.fromEntries(url.searchParams.entries());

      await this.router.navigate([pathname], { queryParams: searchParams });
      this.showMainModal = true;
    }
  }

  ngOnInit(): void {
    this.subscription = this.modalCommunicationService.closeModal$.subscribe(
      () => this.setShowMainModal(false),
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  setShowMainModal(val: boolean) {
    this.showMainModal = val;
  }
}
